import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Sankey, Layer, Rectangle,
} from 'recharts';
import {
  Users, Gamepad2, DollarSign, Activity, Shield,
  RefreshCw, AlertTriangle, CheckCircle, Zap, Target,
  Eye, Award, Coins, Server, TrendingUp, TrendingDown,
  Wallet, ArrowUpRight, ArrowDownRight, Clock, UserPlus,
  CreditCard, Ban, Play, Globe, Filter, Heart, Trophy,
  ShieldCheck, Cpu, Layers, Brain,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminService } from '../services/adminService';
import type { Country } from '../types';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  formatCurrency, formatNumber, formatPercent, formatCompactNumber, safeNumber,
} from '../utils/format';
import './Dashboard.css';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, changeType, icon, color, loading, subtitle }) => (
  <div className="metric-card" style={{ borderLeftColor: color }}>
    <div className="metric-icon" style={{ backgroundColor: `${color}15`, color }}>
      {icon}
    </div>
    <div className="metric-content">
      <span className="metric-title">{title}</span>
      {loading ? (
        <div className="metric-skeleton" />
      ) : (
        <>
          <span className="metric-value">{value}</span>
          {subtitle && <span className="metric-subtitle">{subtitle}</span>}
          {change && (
            <span className={`metric-change ${changeType}`}>
              {changeType === 'positive' ? <TrendingUp size={12} /> :
                changeType === 'negative' ? <TrendingDown size={12} /> : null}
              {change}
            </span>
          )}
        </>
      )}
    </div>
  </div>
);

interface StatusIndicatorProps {
  label: string;
  status: 'healthy' | 'warning' | 'error' | 'loading' | 'unconfigured';
  detail?: string;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ label, status, detail }) => (
  <div className="status-indicator">
    <div className={`status-dot ${status}`}>
      {status === 'loading' ? (
        <RefreshCw className="spin" size={12} />
      ) : status === 'healthy' ? (
        <CheckCircle size={12} />
      ) : status === 'unconfigured' ? (
        <Filter size={12} />
      ) : (
        <AlertTriangle size={12} />
      )}
    </div>
    <span className="status-label">{label}</span>
    {detail && <span className="status-detail">{detail}</span>}
  </div>
);

interface ActivityItem {
  id: string;
  type: 'user_signup' | 'session_completed' | 'withdrawal' | 'fraud_flag' | 'session_started' | 'campaign' | 'settlement';
  description: string;
  timestamp: Date;
  metadata?: any;
}

const ActivityFeed: React.FC<{ activities: ActivityItem[] }> = ({ activities }) => {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_signup': return <UserPlus size={16} className="activity-icon signup" />;
      case 'session_completed': return <Award size={16} className="activity-icon completed" />;
      case 'withdrawal': return <CreditCard size={16} className="activity-icon withdrawal" />;
      case 'fraud_flag': return <Ban size={16} className="activity-icon fraud" />;
      case 'session_started': return <Play size={16} className="activity-icon started" />;
      case 'campaign': return <Heart size={16} className="activity-icon" />;
      case 'settlement': return <DollarSign size={16} className="activity-icon" />;
      default: return <Activity size={16} className="activity-icon" />;
    }
  };

  return (
    <div className="activity-feed">
      {activities.length === 0 ? (
        <div className="activity-empty">No recent activity</div>
      ) : (
        activities.map((activity) => (
          <div key={activity.id} className="activity-item">
            {getActivityIcon(activity.type)}
            <div className="activity-content">
              <span className="activity-description">{activity.description}</span>
              <span className="activity-time">
                <Clock size={12} />
                {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// === Funnel widget (horizontal stacked bars) ===
interface FunnelData {
  signups: number;
  email_verified: number;
  first_game: number;
  first_win: number;
  first_withdrawal: number;
}

const FunnelWidget: React.FC<{ data: FunnelData | null; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="chart-skeleton" />;
  if (!data || data.signups === 0) return <div className="chart-empty">No signups in this window</div>;
  const stages = [
    { key: 'signups', label: 'Signups', value: data.signups, color: '#410C6D' },
    { key: 'email_verified', label: 'Email Verified', value: data.email_verified, color: '#22c55e' },
    { key: 'first_game', label: 'First Session', value: data.first_game, color: '#f59e0b' },
    { key: 'first_win', label: 'First Win', value: data.first_win, color: '#ec4899' },
    { key: 'first_withdrawal', label: 'First Cashout', value: data.first_withdrawal, color: '#10b981' },
  ];
  const max = stages[0].value || 1;
  return (
    <div className="funnel-bars">
      {stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        const conv = i === 0 ? 100 : (s.value / stages[i - 1].value) * 100;
        return (
          <div key={s.key} className="funnel-row">
            <div className="funnel-label">{s.label}</div>
            <div className="funnel-bar-track">
              <div
                className="funnel-bar-fill"
                style={{ width: `${Math.max(pct, 4)}%`, background: s.color }}
              />
              <span className="funnel-value">{formatNumber(s.value)}</span>
            </div>
            <div className="funnel-conversion">
              {i === 0 ? '—' : `${conv.toFixed(1)}%`}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// === Cohort retention table (heatmap-style) ===
const CohortTable: React.FC<{ rows: any[]; loading: boolean }> = ({ rows, loading }) => {
  if (loading) return <div className="chart-skeleton" />;
  if (!rows || rows.length === 0) return <div className="chart-empty">No cohort data</div>;
  const cellColor = (pct: number) => {
    const v = Math.max(0, Math.min(100, pct)) / 100;
    const alpha = 0.1 + v * 0.6;
    return `rgba(99, 102, 241, ${alpha.toFixed(2)})`;
  };
  return (
    <div className="cohort-table-wrap">
      <table className="cohort-table">
        <thead>
          <tr>
            <th>Week of</th>
            <th>Cohort</th>
            <th>D1</th>
            <th>D7</th>
            <th>D30</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cohort_week}>
              <td>{r.cohort_week}</td>
              <td>{formatNumber(r.cohort_size)}</td>
              <td style={{ background: cellColor(r.d1_pct ?? 0) }}>
                {formatPercent(r.d1_pct, 1)}
                <span className="cohort-cell-count">{formatNumber(r.d1)}</span>
              </td>
              <td style={{ background: cellColor(r.d7_pct ?? 0) }}>
                {formatPercent(r.d7_pct, 1)}
                <span className="cohort-cell-count">{formatNumber(r.d7)}</span>
              </td>
              <td style={{ background: cellColor(r.d30_pct ?? 0) }}>
                {formatPercent(r.d30_pct, 1)}
                <span className="cohort-cell-count">{formatNumber(r.d30)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Custom Sankey node renderer
const SankeyNode = (props: any) => {
  const { x, y, width, height, index, payload } = props;
  const palette = ['#410C6D', '#22c55e', '#f59e0b', '#ec4899', '#10b981', '#5a1494'];
  const color = palette[index % palette.length];
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.9} />
      <text
        x={x + width + 6}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="middle"
        fill="#374151"
        fontSize={11}
        fontWeight={600}
      >
        {payload?.name}
      </text>
    </Layer>
  );
};

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState(7);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | undefined>(undefined);

  // Existing data states
  const [summary, setSummary] = useState<any>(null);
  const [dauData, setDauData] = useState<any[]>([]);
  const [sessionData, setSessionData] = useState<any[]>([]);
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([]);
  const [financialMetrics, setFinancialMetrics] = useState({
    totalWithdrawals: 0,
    pendingWithdrawals: 0,
    totalDeposits: 0,
    netRevenue: 0,
  });
  const [systemStatus, setSystemStatus] = useState<{
    api: any; redis: any; database: any;
  }>({ api: 'loading', redis: 'loading', database: 'loading' });

  // New extension states
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [retention, setRetention] = useState<any[]>([]);
  const [online, setOnline] = useState<any | null>(null);
  const [providers, setProviders] = useState<any[]>([]);
  const [adEventsSummary, setAdEventsSummary] = useState<any | null>(null);
  const [bioStats, setBioStats] = useState<any | null>(null);
  const [bioTimeseries, setBioTimeseries] = useState<any[]>([]);
  const [topEarners, setTopEarners] = useState<any[]>([]);
  const [currencyData, setCurrencyData] = useState<any | null>(null);
  const [complianceSummary, setComplianceSummary] = useState<any | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [proStats, setProStats] = useState<any>(null);

  const selectedCurrency = useMemo(() => {
    const c = countries.find((x) => x.id === selectedCountry);
    return c?.currency_code || 'NGN';
  }, [countries, selectedCountry]);

  const loadCountries = useCallback(async () => {
    try {
      const list = await adminService.getCountries();
      setCountries(list);
    } catch (err) {
      console.warn('failed to load countries', err);
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      const [
        summaryRes, dauRes, gameRes, revenueRes, withdrawalRes,
        funnelRes, retentionRes, adEventsRes, fraudStatsRes,
        topRes, currencyRes, complianceRes, campaignsRes, proStatsRes,
      ] = await Promise.allSettled([
        adminService.getAnalyticsSummary(timeRange),
        adminService.getDailyActiveUsers(timeRange),
        adminService.getGameAnalytics(timeRange),
        adminService.getHouseMargins(timeRange),
        adminService.getPendingWithdrawals(),
        adminService.getUserFunnel(timeRange, selectedCountry),
        adminService.getRetentionMatrix(8),
        adminService.getAdEventsSummary(timeRange),
        adminService.getFraudStats(24),
        adminService.getLeaderboardAllTime(5).catch(() => null),
        adminService.getCurrencyAnalytics(timeRange),
        adminService.getComplianceAnalytics(timeRange),
        adminService.getCampaigns({ active_only: true }).catch(() => null),
        adminService.getProStats().catch(() => null),
      ]);

      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      if (dauRes.status === 'fulfilled') {
        setDauData(dauRes.value?.map((d: any) => ({
          ...d, date: format(parseISO(d.date), 'MMM dd'),
        })) || []);
      }
      if (gameRes.status === 'fulfilled') {
        setSessionData(gameRes.value?.map((d: any) => ({
          ...d, date: format(parseISO(d.date), 'MMM dd'),
        })) || []);
      }
      if (revenueRes.status === 'fulfilled') {
        const revenue = (revenueRes.value || []).map((d: any) => ({
          ...d,
          date: format(parseISO(d.date), 'MMM dd'),
          ad_revenue: safeNumber(d.ad_revenue),
          platform_margin: safeNumber(d.platform_margin),
          total_stakes: safeNumber(d.total_stakes),
          total_payouts: safeNumber(d.total_payouts),
          house_profit: safeNumber(d.house_profit),
        }));
        setRevenueData(revenue);
        const totalAd = revenue.reduce((acc: number, d: any) => acc + d.ad_revenue, 0);
        setFinancialMetrics((prev) => ({ ...prev, netRevenue: totalAd }));
      }
      if (withdrawalRes.status === 'fulfilled') {
        const list = Array.isArray(withdrawalRes.value)
          ? withdrawalRes.value
          : withdrawalRes.value?.withdrawals || withdrawalRes.value?.data || [];
        const pending = list.filter((w: any) => w.status === 'pending');
        const completed = list.filter((w: any) => w.status === 'completed' || w.status === 'approved');
        const pendingTotal = pending.reduce((a: number, w: any) => a + safeNumber(w.amount), 0);
        const completedTotal = completed.reduce((a: number, w: any) => a + safeNumber(w.amount), 0);
        setFinancialMetrics((prev) => ({
          ...prev, pendingWithdrawals: pendingTotal, totalWithdrawals: completedTotal,
        }));
      }
      if (funnelRes.status === 'fulfilled') setFunnel(funnelRes.value);
      if (retentionRes.status === 'fulfilled') setRetention(retentionRes.value?.cohorts || []);
      if (adEventsRes.status === 'fulfilled') setAdEventsSummary(adEventsRes.value);
      if (fraudStatsRes.status === 'fulfilled') setBioStats(fraudStatsRes.value);
      if (topRes.status === 'fulfilled' && topRes.value) {
        const items = Array.isArray(topRes.value) ? topRes.value : topRes.value?.items || [];
        setTopEarners(items.slice(0, 5));
      }
      if (currencyRes.status === 'fulfilled') setCurrencyData(currencyRes.value);
      if (complianceRes.status === 'fulfilled') setComplianceSummary(complianceRes.value);
      if (campaignsRes.status === 'fulfilled' && campaignsRes.value) {
        const list = Array.isArray(campaignsRes.value) ? campaignsRes.value : campaignsRes.value?.items || [];
        setCampaigns(list);
      }
      if (proStatsRes.status === 'fulfilled' && proStatsRes.value) {
        setProStats(proStatsRes.value);
      }

      // Activity feed: real signals
      const activities: ActivityItem[] = [];
      if (withdrawalRes.status === 'fulfilled') {
        const list = Array.isArray(withdrawalRes.value)
          ? withdrawalRes.value
          : withdrawalRes.value?.withdrawals || withdrawalRes.value?.data || [];
        list.slice(0, 8).forEach((w: any, i: number) => {
          activities.push({
            id: `wd-${w.id || i}`,
            type: 'withdrawal',
            description: `Withdrawal ${w.status || 'requested'}: ${formatCurrency(w.amount, w.currency_code || selectedCurrency)} by user #${w.user_id}`,
            timestamp: new Date(w.created_at || w.requested_at || Date.now()),
          });
        });
      }
      try {
        const flags: any = await adminService.getFraudFlags();
        const flagList: any[] = Array.isArray(flags) ? flags : flags?.items || [];
        flagList.slice(0, 5).forEach((f: any) => {
          activities.push({
            id: `fl-${f.id}`,
            type: 'fraud_flag',
            description: `Fraud flag (${f.severity || 'info'}): ${f.description || f.flag_type} for user #${f.user_id}`,
            timestamp: new Date(f.created_at || Date.now()),
          });
        });
      } catch { /* ignore */ }
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setRecentActivities(activities.slice(0, 25));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange, selectedCountry, selectedCurrency]);

  const checkSystemStatus = useCallback(async () => {
    try {
      const h = await adminService.healthCheck();
      const dbStatus = h?.services?.database === 'connected' ? 'healthy' : 'warning';
      setSystemStatus((prev) => ({ ...prev, api: 'healthy', database: dbStatus }));
    } catch {
      setSystemStatus((prev) => ({ ...prev, api: 'error', database: 'error' }));
    }
    try {
      const r = await adminService.getRedisStatus();
      setSystemStatus((prev) => ({
        ...prev, redis: r?.status === 'connected' ? 'healthy' : 'warning',
      }));
    } catch {
      setSystemStatus((prev) => ({ ...prev, redis: 'warning' }));
    }
    try {
      const ph = await adminService.getProvidersHealth();
      setProviders(ph?.providers || []);
    } catch {
      setProviders([]);
    }
  }, []);

  const loadLiveKpis = useCallback(async () => {
    try {
      const [onlineRes] = await Promise.allSettled([
        adminService.getOnlineUsers(),
      ]);
      if (onlineRes.status === 'fulfilled') setOnline(onlineRes.value);
    } catch { /* ignore */ }
  }, []);

  const loadBioTimeseries = useCallback(async () => {
    try {
      const ts = await adminService.getAdEventsTimeseries(timeRange);
      setBioTimeseries(ts?.series || []);
    } catch { /* ignore */ }
  }, [timeRange]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    loadDashboardData();
    checkSystemStatus();
    loadLiveKpis();
    loadBioTimeseries();
    const slow = setInterval(() => {
      loadDashboardData();
      checkSystemStatus();
    }, 60000);
    const fast = setInterval(loadLiveKpis, 15000);
    return () => { clearInterval(slow); clearInterval(fast); };
  }, [loadDashboardData, checkSystemStatus, loadLiveKpis, loadBioTimeseries]);

  const handleRefresh = () => {
    loadDashboardData();
    checkSystemStatus();
    loadLiveKpis();
    loadBioTimeseries();
  };

  // Derived metrics
  const totalSessions = sessionData.reduce((acc, d) => acc + (d.total_games || 0), 0);
  const avgCompletionRate = sessionData.length > 0
    ? (sessionData.reduce((acc, d) => acc + parseFloat(d.completion_rate || 0), 0) / sessionData.length)
    : 0;
  const totalStandardSessions = sessionData.reduce((acc, d) => acc + (d.demo_games || 0), 0);
  const totalAdvancedSessions = sessionData.reduce((acc, d) => acc + (d.multiply_games || 0), 0);
  const protocolData = [
    { name: 'Standard Protocol', value: totalStandardSessions, color: '#410C6D' },
    { name: 'Advanced Protocol', value: totalAdvancedSessions, color: '#22c55e' },
  ].filter(d => d.value > 0);

  // Money flow Sankey nodes/links
  const sankey = useMemo(() => {
    const stakes = revenueData.reduce((a, d) => a + safeNumber(d.total_stakes), 0);
    const payouts = revenueData.reduce((a, d) => a + safeNumber(d.total_payouts), 0);
    const adRev = revenueData.reduce((a, d) => a + safeNumber(d.ad_revenue), 0);
    const platform = Math.max(0, stakes - payouts) + adRev;
    const cashouts = financialMetrics.totalWithdrawals;
    const total = stakes + adRev;
    if (total <= 0) return null;
    const nodes = [
      { name: 'Players' }, { name: 'Stakes' }, { name: 'Ad Revenue' },
      { name: 'Wins' }, { name: 'Platform' }, { name: 'Cashouts' },
    ];
    const links = [
      { source: 0, target: 1, value: Math.max(stakes, 1) },
      { source: 0, target: 2, value: Math.max(adRev, 1) },
      { source: 1, target: 3, value: Math.max(payouts, 1) },
      { source: 1, target: 4, value: Math.max(stakes - payouts, 1) },
      { source: 2, target: 4, value: Math.max(adRev, 1) },
      { source: 3, target: 5, value: Math.max(cashouts, 1) },
    ].filter((l) => l.value > 0);
    return { nodes, links, totals: { stakes, payouts, adRev, platform, cashouts } };
  }, [revenueData, financialMetrics.totalWithdrawals]);

  // Tasks/offerwall mini summary
  const offerwall = useMemo(() => {
    if (!adEventsSummary) return null;
    const total = Object.values(adEventsSummary.counts_by_type || {})
      .reduce((a: number, n: any) => a + safeNumber(n), 0);
    const completion = (adEventsSummary.rewarded_completion_rate || 0) * 100;
    return {
      totalEvents: total as number,
      completion,
      avgEcpm: adEventsSummary.avg_ecpm || 0,
    };
  }, [adEventsSummary]);

  // Currency analytics top countries
  const currencyTop = useMemo(() => {
    if (!currencyData) return [] as Array<{ code: string; volume: number }>;
    const map = (currencyData.conversion_volume_by_currency
      || currencyData.by_currency
      || currencyData.totals_by_currency
      || {}) as Record<string, number>;
    return Object.entries(map)
      .map(([code, volume]) => ({ code, volume: safeNumber(volume) }))
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 5);
  }, [currencyData]);

  // Compliance summary
  const compliance = useMemo(() => {
    if (!complianceSummary) return null;
    return {
      skillClassified: complianceSummary.skill_classified_users
        ?? complianceSummary.skill_based_classification?.classified_users
        ?? 0,
      avgQuiz: complianceSummary.average_quiz_accuracy
        ?? complianceSummary.quiz_accuracy_avg
        ?? complianceSummary.skill_based_classification?.average_quiz_accuracy
        ?? 0,
      pctSkill: complianceSummary.skill_based_pct
        ?? complianceSummary.skill_classified_pct
        ?? 0,
    };
  }, [complianceSummary]);

  // Active campaigns count
  const activeCampaigns = (campaigns || []).filter((c: any) => c.is_active).length;

  return (
    <div className="dashboard-page-modern">
      {/* Header + filter bar */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard</h1>
          <p className="header-subtitle">Welcome back! Here's what's happening with Trinze.</p>
        </div>
        <div className="header-right">
          <div className="dashboard-filter-bar">
            <div className="filter-group">
              <Filter size={14} />
              <select
                className="time-range-select"
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
              >
                <option value={1}>Last 24h</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
            <div className="filter-group">
              <Globe size={14} />
              <select
                className="time-range-select"
                value={selectedCountry ?? ''}
                onChange={(e) => setSelectedCountry(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">All countries</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={refreshing ? 'spin' : ''} size={18} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Live KPI strip (auto refresh 15s) */}
      <div className="live-kpi-strip">
        <div className="live-kpi">
          <Activity size={14} />
          <span className="kpi-label">Online now</span>
          <span className="kpi-value">{formatCompactNumber(online?.online_now ?? 0)}</span>
        </div>
        <div className="live-kpi">
          <Gamepad2 size={14} />
          <span className="kpi-label">Active sessions</span>
          <span className="kpi-value">{formatCompactNumber(online?.active_sessions ?? 0)}</span>
        </div>
        <div className="live-kpi">
          <Wallet size={14} />
          <span className="kpi-label">Pending withdrawals</span>
          <span className="kpi-value">{formatCompactNumber(online?.pending_withdrawals ?? 0)}</span>
          {online?.oldest_pending_minutes ? (
            <span className="kpi-detail">oldest {online.oldest_pending_minutes}m</span>
          ) : null}
        </div>
        <div className="live-kpi">
          <Zap size={14} />
          <span className="kpi-label">Tasks (24h)</span>
          <span className="kpi-value">{formatCompactNumber(offerwall?.totalEvents ?? 0)}</span>
        </div>
        <div className="live-kpi">
          <ShieldCheck size={14} />
          <span className="kpi-label">Flag rate</span>
          <span className="kpi-value">{formatPercent(bioStats?.flag_rate_percent, 2)}</span>
        </div>
      </div>

      {/* System / providers health bar */}
      <div className="system-status-bar">
        <div className="status-bar-title">
          <Server size={16} /> System & Providers
        </div>
        <div className="status-indicators">
          <StatusIndicator label="API" status={systemStatus.api} />
          <StatusIndicator label="Database" status={systemStatus.database} />
          <StatusIndicator label="Redis" status={systemStatus.redis} />
          {providers.map((p: any) => {
            const map: Record<string, any> = {
              healthy: 'healthy', degraded: 'warning', down: 'error',
              unconfigured: 'unconfigured', unknown: 'warning',
            };
            return (
              <StatusIndicator
                key={p.provider}
                label={p.provider}
                status={map[p.status] || 'warning'}
                detail={p.balance != null ? `${formatCompactNumber(p.balance)} ${p.balance_currency || ''}` : undefined}
              />
            );
          })}
        </div>
      </div>

      {/* Primary metrics */}
      <div className="metrics-grid">
        <MetricCard
          title="Total Users"
          value={summary?.total_users != null ? formatNumber(summary.total_users) : '—'}
          icon={<Users size={24} />} color="#a855f7" loading={loading}
        />
        <MetricCard
          title="Total Sessions"
          value={formatNumber(totalSessions)}
          subtitle={`${formatNumber(totalStandardSessions)} Standard • ${formatNumber(totalAdvancedSessions)} Advanced`}
          icon={<Gamepad2 size={24} />} color="#22c55e" loading={loading}
        />
        <MetricCard
          title="Media Revenue"
          value={financialMetrics.netRevenue > 0 ? formatCurrency(financialMetrics.netRevenue, selectedCurrency) : '—'}
          icon={<DollarSign size={24} />} color="#f59e0b" loading={loading}
        />
        <MetricCard
          title="Completion Rate"
          value={avgCompletionRate > 0 ? formatPercent(avgCompletionRate) : '—'}
          icon={<Target size={24} />} color="#a855f7" loading={loading}
          change={avgCompletionRate > 70 ? 'Above target' : 'Below target'}
          changeType={avgCompletionRate > 70 ? 'positive' : 'negative'}
        />
      </div>

      {/* Financial overview */}
      <div className="section-title">
        <Wallet size={20} /> <h2>Financial Overview</h2>
      </div>
      <div className="metrics-grid four-cols">
        <MetricCard
          title="Platform Margin"
          value={revenueData.length > 0
            ? formatCurrency(revenueData.reduce((acc, d) => acc + (d.platform_margin || 0), 0), selectedCurrency)
            : '—'}
          icon={<TrendingUp size={24} />} color="#10b981" loading={loading}
        />
        <MetricCard
          title="Pending Withdrawals"
          value={formatCurrency(financialMetrics.pendingWithdrawals, selectedCurrency)}
          icon={<ArrowUpRight size={24} />} color="#f97316" loading={loading}
        />
        <MetricCard
          title="Total Payouts"
          value={formatCurrency(financialMetrics.totalWithdrawals, selectedCurrency)}
          subtitle="This period"
          icon={<ArrowDownRight size={24} />} color="#ef4444" loading={loading}
        />
        <MetricCard
          title="Active Campaigns"
          value={formatNumber(activeCampaigns)}
          subtitle={`${campaigns.length} total`}
          icon={<Heart size={24} />} color="#a855f7" loading={loading}
        />
      </div>

      {/* Pro Workspace */}
      <div className="section-title">
        <Brain size={20} /> <h2>Pro Workspace</h2>
      </div>
      <div className="metrics-grid four-cols">
        <MetricCard
          title="Active Sessions"
          value={proStats?.active_sessions ?? '—'}
          icon={<Zap size={24} />} color="#8b5cf6" loading={loading}
        />
        <MetricCard
          title="Sessions Today"
          value={proStats?.sessions_today ?? '—'}
          icon={<Trophy size={24} />} color="#22c55e" loading={loading}
        />
        <MetricCard
          title="TP Awarded Today"
          value={proStats?.tp_awarded_today != null ? formatNumber(proStats.tp_awarded_today) : '—'}
          icon={<Coins size={24} />} color="#f59e0b" loading={loading}
        />
        <MetricCard
          title="Questions Live"
          value={proStats?.questions_live ?? '—'}
          subtitle={proStats?.questions_review > 0 ? `${proStats.questions_review} pending review` : `${proStats?.questions_total ?? 0} total`}
          icon={<Brain size={24} />} color="#6366f1" loading={loading}
        />
      </div>
      {proStats?.questions_review > 0 && (
        <div className="pro-review-alert">
          <AlertTriangle size={14} />
          {proStats.questions_review} question{proStats.questions_review > 1 ? 's' : ''} pending review —{' '}
          <Link to="/dashboard/pro">Review now</Link>
        </div>
      )}

      {/* Funnel + Money flow row */}
      <div className="charts-row">
        <div className="chart-card funnel-card">
          <div className="chart-header">
            <h3><Layers size={18} /> User Funnel ({timeRange}d)</h3>
            <Link to="/dashboard/users" className="view-all">All users →</Link>
          </div>
          <FunnelWidget data={funnel} loading={loading} />
        </div>
        <div className="chart-card sankey-card">
          <div className="chart-header">
            <h3><Coins size={18} /> Money Flow</h3>
            {sankey?.totals && (
              <span className="card-subtitle">
                Stakes {formatCurrency(sankey.totals.stakes, selectedCurrency)} →
                Cashouts {formatCurrency(sankey.totals.cashouts, selectedCurrency)}
              </span>
            )}
          </div>
          <div className="chart-body">
            {!sankey ? (
              <div className="chart-empty">Not enough data for money flow</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <Sankey
                  data={sankey}
                  nodePadding={28}
                  nodeWidth={12}
                  margin={{ top: 8, right: 80, bottom: 8, left: 8 }}
                  link={{ stroke: '#cbd5e1' }}
                  node={<SankeyNode />}
                >
                  <Tooltip />
                </Sankey>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Cohort retention + Top performers */}
      <div className="charts-row">
        <div className="chart-card cohort-card">
          <div className="chart-header">
            <h3><Users size={18} /> Retention by Cohort</h3>
          </div>
          <CohortTable rows={retention} loading={loading} />
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><Trophy size={18} /> Top Earners (all-time)</h3>
            <Link to="/dashboard/leaderboard" className="view-all">Leaderboard →</Link>
          </div>
          <div className="top-performers">
            {topEarners.length === 0 ? (
              <div className="chart-empty">No leaderboard data yet</div>
            ) : topEarners.map((u: any, i: number) => (
              <div key={u.user_id || i} className="top-performer-row">
                <span className="rank">#{u.rank ?? i + 1}</span>
                <span className="name">{u.full_name || u.email || `User ${u.user_id}`}</span>
                <span className="score">{formatNumber(u.score ?? u.earnings ?? 0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts: DAU + Protocol */}
      <div className="charts-row">
        <div className="chart-card wide">
          <div className="chart-header">
            <h3><Activity size={18} /> Daily Active Users</h3>
          </div>
          <div className="chart-body">
            {loading ? <div className="chart-skeleton" /> : dauData.length === 0 ? (
              <div className="chart-empty">No data available for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={dauData}>
                  <defs>
                    <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#410C6D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#410C6D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                  <Area type="monotone" dataKey="active_users" stroke="#410C6D" fill="url(#dauGradient)" strokeWidth={2} name="Active Users" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><Award size={18} /> Protocol Type</h3>
          </div>
          <div className="chart-body">
            {loading ? <div className="chart-skeleton" /> : protocolData.length === 0 ? (
              <div className="chart-empty">No sessions started yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={protocolData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100}
                    paddingAngle={5} dataKey="value"
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {protocolData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Charts: Sessions + Revenue */}
      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-header">
            <h3><Gamepad2 size={18} /> Session Status</h3>
          </div>
          <div className="chart-body">
            {loading ? <div className="chart-skeleton" /> : sessionData.length === 0 ? (
              <div className="chart-empty">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={sessionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                  <Legend />
                  <Bar dataKey="total_games" name="Total Sessions" fill="#410C6D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed_games" name="Completed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><Coins size={18} /> Revenue Trend</h3>
          </div>
          <div className="chart-body">
            {loading ? <div className="chart-skeleton" /> : revenueData.length === 0 ? (
              <div className="chart-empty">No revenue data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }}
                    formatter={(value: any) => [formatCurrency(value, selectedCurrency), '']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="ad_revenue" name="Media Revenue" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', strokeWidth: 2 }} />
                  <Line type="monotone" dataKey="platform_margin" name="Platform Margin" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', strokeWidth: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Mini cards: Tasks, Bio-Kinetic, Compliance, Currency */}
      <div className="mini-cards-grid">
        <div className="mini-card">
          <div className="mini-card-header">
            <Cpu size={16} /> <h4>Tasks / Offerwall</h4>
            <Link to="/dashboard/tasks" className="view-all-mini">→</Link>
          </div>
          <div className="mini-card-body">
            <div className="mini-stat">
              <span className="mini-stat-label">Total events</span>
              <span className="mini-stat-value">{formatCompactNumber(offerwall?.totalEvents ?? 0)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">Completion</span>
              <span className="mini-stat-value">{formatPercent(offerwall?.completion ?? 0, 1)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">Avg eCPM</span>
              <span className="mini-stat-value">${(offerwall?.avgEcpm ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="mini-card">
          <div className="mini-card-header">
            <Shield size={16} /> <h4>Bio-Kinetic Fraud (24h)</h4>
            <Link to="/dashboard/biokinetic" className="view-all-mini">→</Link>
          </div>
          <div className="mini-card-body">
            <div className="mini-stat">
              <span className="mini-stat-label">Games analyzed</span>
              <span className="mini-stat-value">{formatCompactNumber(bioStats?.total_games_analyzed ?? 0)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">Flagged</span>
              <span className="mini-stat-value">{formatCompactNumber(bioStats?.flagged_games ?? 0)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">Avg trust</span>
              <span className="mini-stat-value">{bioStats?.average_trust_score != null ? Number(bioStats.average_trust_score).toFixed(1) : '—'}</span>
            </div>
          </div>
          {bioTimeseries.length > 0 && (
            <ResponsiveContainer width="100%" height={50}>
              <LineChart data={bioTimeseries}>
                <Line type="monotone" dataKey="avg_ecpm" stroke="#5a1494" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mini-card">
          <div className="mini-card-header">
            <Award size={16} /> <h4>Compliance</h4>
            <Link to="/dashboard/compliance" className="view-all-mini">→</Link>
          </div>
          <div className="mini-card-body">
            <div className="mini-stat">
              <span className="mini-stat-label">Skill-classified</span>
              <span className="mini-stat-value">{formatCompactNumber(compliance?.skillClassified ?? 0)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">% Skill</span>
              <span className="mini-stat-value">{formatPercent(compliance?.pctSkill ?? 0, 1)}</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-label">Avg quiz acc.</span>
              <span className="mini-stat-value">{formatPercent((compliance?.avgQuiz ?? 0) * (Number(compliance?.avgQuiz ?? 0) <= 1 ? 100 : 1), 1)}</span>
            </div>
          </div>
        </div>
        <div className="mini-card">
          <div className="mini-card-header">
            <Globe size={16} /> <h4>Currency Flow</h4>
          </div>
          <div className="mini-card-body">
            {currencyTop.length === 0 ? (
              <div className="chart-empty">No conversions yet</div>
            ) : currencyTop.map((c) => (
              <div key={c.code} className="mini-stat">
                <span className="mini-stat-label">{c.code}</span>
                <span className="mini-stat-value">{formatCompactNumber(c.volume)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity feed + quick actions */}
      <div className="bottom-section">
        <div className="activity-card">
          <div className="card-header">
            <h3><Clock size={18} /> Recent Activity</h3>
            <Link to="/dashboard/audit" className="view-all">Audit log →</Link>
          </div>
          <ActivityFeed activities={recentActivities} />
        </div>

        <div className="quick-actions-card">
          <div className="card-header">
            <h3><Zap size={18} /> Quick Actions</h3>
          </div>
          <div className="quick-actions-list">
            <Link to="/dashboard/users" className="quick-action-item">
              <Users size={20} /> <span>Manage Users</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
            <Link to="/dashboard/withdrawals" className="quick-action-item">
              <Wallet size={20} /> <span>Review Withdrawals</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
            <Link to="/dashboard/biokinetic" className="quick-action-item">
              <Shield size={20} /> <span>Bio-Kinetic</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
            <Link to="/dashboard/providers" className="quick-action-item">
              <Server size={20} /> <span>Provider Health</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
            <Link to="/dashboard/health" className="quick-action-item">
              <Eye size={20} /> <span>System Health</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
            <Link to="/dashboard/tasks" className="quick-action-item">
              <Cpu size={20} /> <span>Tasks / Offerwall</span> <ArrowUpRight size={16} className="arrow" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
